import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { Alert, Button, Chip, Snackbar, Stack, Tab, Tabs } from '@mui/material';
import axios from 'axios';
import React from 'react';
import { Redirect, Route, Switch as RouterSwitch, useHistory, useParams } from 'react-router-dom';
import {
    IVerbaScholaCourse,
    IVerbaScholaLesson,
    IVerbaScholaStudentCourse,
    IVerbaScholaTeacherCourse,
    lessonPublicationError,
    VerbaScholaApi,
} from '../api/VerbaScholaApi';
import VerbaScholaTeacherShell from '../component/VerbaScholaTeacherShell';
import useVerbaScholaResource from '../hook/useVerbaScholaResource';
import { aiButtonSx, BLUE, INTER, SLATE, SUCCESS_SOFT, SUCCESS_TEXT, WARNING_SOFT, WARNING_TEXT } from '../style/verbaScholaStyles';
import { meetingDaysLabel, startTimeLabel } from '../util/verbaScholaCourseSchedule';
import VerbaScholaTeacherCourseLessonsPage from './VerbaScholaTeacherCourseLessonsPage';
import VerbaScholaTeacherCourseOverviewPage from './VerbaScholaTeacherCourseOverviewPage';
import VerbaScholaTeacherCourseSettingsPage from './VerbaScholaTeacherCourseSettingsPage';
import VerbaScholaTeacherCourseStudentsPage from './VerbaScholaTeacherCourseStudentsPage';

type CourseTab = 'overview' | 'lesson' | 'student' | 'settings';

const TAB_LIST: { value: CourseTab; label: string }[] = [
    { value: 'overview', label: 'Overview' },
    { value: 'lesson', label: 'Lessons' },
    { value: 'student', label: 'Students' },
    { value: 'settings', label: 'Settings' },
];

function arrayOrEmpty<T>(value: T[] | null | undefined): T[] {
    return Array.isArray(value) ? value : [];
}

function arrayShapeError(label: string, value: unknown): string | null {
    return value != null && !Array.isArray(value) ? `Could not load ${label}.` : null;
}

function invitationDeliveryFailed(data: unknown): boolean {
    return typeof data === 'object' && data !== null && (data as { invitation_sent?: boolean | null }).invitation_sent === false;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
    return `${count} ${count === 1 ? singular : plural}`;
}

function courseSubtitle(course: IVerbaScholaCourse, studentCount: number | undefined): string {
    return [
        pluralize(studentCount ?? 0, 'student'),
        meetingDaysLabel(course.meeting_days),
        startTimeLabel(course.start_time),
        roomLabel(course.room),
    ].filter(Boolean).join(' · ');
}

function roomLabel(room: string | null | undefined): string | null {
    const trimmed = room?.trim();
    if (!trimmed) return null;
    return /^room\b/i.test(trimmed) ? trimmed : `Room ${trimmed}`;
}

function absoluteSignupUrl(course: IVerbaScholaCourse): string | null {
    const rawLink = course.signup_link?.trim();
    if (rawLink) {
        if (/^https?:\/\//i.test(rawLink)) return rawLink;
        return `${window.location.origin}${rawLink.startsWith('/') ? rawLink : `/${rawLink}`}`;
    }

    const signupCode = course.signup_code?.trim();
    return signupCode ? `${window.location.origin}/registration?c=${encodeURIComponent(signupCode)}` : null;
}

function copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied ? Promise.resolve() : Promise.reject(new Error('Clipboard copy failed'));
}

function statusChipSx(status: string) {
    const positive = status === 'accepted' || status === 'published' || status === 'current' || status === 'owner';
    return {
        fontFamily: INTER,
        fontWeight: 600,
        fontSize: 11,
        textTransform: 'capitalize',
        color: positive ? SUCCESS_TEXT : WARNING_TEXT,
        backgroundColor: positive ? SUCCESS_SOFT : WARNING_SOFT,
    };
}

interface VerbaScholaTeacherCourseWorkspaceProps {
    courseId: number;
    activeTab: CourseTab;
}

export default function VerbaScholaTeacherCoursePage() {
    const { courseId: courseIdParam, courseTab } = useParams() as { courseId: string; courseTab?: string };
    const courseId = Number(courseIdParam);
    const activeTab = TAB_LIST.find(tab => tab.value === courseTab)?.value;

    if (!activeTab) return <Redirect to={`/teacher/course/${courseId}/overview`} />;
    return <VerbaScholaTeacherCourseWorkspace key={courseId} courseId={courseId} activeTab={activeTab} />;
}

function VerbaScholaTeacherCourseWorkspace({ courseId, activeTab }: VerbaScholaTeacherCourseWorkspaceProps) {
    const history = useHistory();
    const [message, setMessage] = React.useState<{ text: string; severity: 'success' | 'warning' | 'error' } | null>(null);
    const [pictureReviewLessonId, setPictureReviewLessonId] = React.useState<number | null>(null);
    const [saving, setSaving] = React.useState(false);
    const showMessage = (value: { text: string; severity: 'success' | 'warning' | 'error' } | null) => {
        setPictureReviewLessonId(null);
        setMessage(value);
    };
    const [courseNamePreview, setCourseNamePreview] = React.useState<string | null>(null);

    const overview = useVerbaScholaResource(
        () => VerbaScholaApi.getCourseOverview(courseId),
        [courseId],
        'That class does not exist.',
    );
    const lessonList = useVerbaScholaResource<IVerbaScholaLesson[]>(
        () => VerbaScholaApi.getCourseLessonList(courseId),
        [courseId],
        'That class does not exist.',
    );
    const studentList = useVerbaScholaResource<IVerbaScholaStudentCourse[]>(
        () => VerbaScholaApi.getCourseStudentList(courseId),
        [courseId],
        'That class does not exist.',
    );
    const teacherList = useVerbaScholaResource<IVerbaScholaTeacherCourse[]>(
        () => VerbaScholaApi.getCourseTeacherList(courseId),
        [courseId],
        'That class does not exist.',
    );

    const active = activeTab === 'lesson' ? lessonList : activeTab === 'student' ? studentList : activeTab === 'settings' ? teacherList : overview;
    const course = overview.data?.course;
    const summary = overview.data?.summary;
    const overviewLessonRows = arrayOrEmpty(overview.data?.lessons);
    const lessonRows = arrayOrEmpty(lessonList.data);
    const studentRows = arrayOrEmpty(studentList.data);
    const teacherRows = arrayOrEmpty(teacherList.data);
    const lessonGroupsById = new Map(overviewLessonRows.map(group => [group.lesson.id, group]));
    const studentSignupUrl = course ? absoluteSignupUrl(course) : null;
    const dataShapeError =
        arrayShapeError('course lessons', overview.data?.lessons) ??
        arrayShapeError('lessons', lessonList.data) ??
        arrayShapeError('students', studentList.data) ??
        arrayShapeError('teachers', teacherList.data);
    const handleNamePreview = React.useCallback((name: string) => setCourseNamePreview(name), []);
    const headerTitle = ((activeTab === 'settings' ? courseNamePreview : null) ?? course?.name ?? '').trim() || 'Class';

    React.useEffect(() => {
        setCourseNamePreview(course?.name ?? null);
    }, [course?.id, course?.name]);

    React.useEffect(() => {
        if (activeTab !== 'settings') setCourseNamePreview(null);
    }, [activeTab]);

    function reloadCourseData() {
        overview.reload();
        lessonList.reload();
        studentList.reload();
        teacherList.reload();
    }

    function runAction(action: () => Promise<unknown>, successMessage: string, invitationHint = 'use Resend invitation if needed.', reviewLessonId?: number): Promise<boolean> {
        if (saving) return Promise.resolve(false);
        setSaving(true);
        return action()
            .then(response => {
                const data = typeof response === 'object' && response !== null && 'data' in response
                    ? (response as { data: unknown }).data
                    : null;
                const deliveryFailed = invitationDeliveryFailed(data);
                showMessage({
                    text: deliveryFailed ? `${successMessage} Invitation email could not be sent; ${invitationHint}` : successMessage,
                    severity: deliveryFailed ? 'warning' : 'success',
                });
                reloadCourseData();
                return true;
            })
            .catch(error => {
                setPictureReviewLessonId(axios.isAxiosError(error) && error.response?.data?.message === 'lesson_pictures_need_review' ? reviewLessonId ?? null : null);
                setMessage({ text: lessonPublicationError(error) ?? 'Could not save changes.', severity: 'error' });
                return false;
            })
            .finally(() => setSaving(false));
    }

    function handleCreateLesson() {
        if (saving) return;
        const orderIndex = overviewLessonRows.reduce((largest, group) => Math.max(largest, group.lesson.order_index ?? 0), 0) + 1;
        setSaving(true);
        VerbaScholaApi.createLesson(courseId, {
            title: 'Untitled lesson',
            description: null,
            brief: null,
            order_index: orderIndex,
            status: 'draft',
            exercise_duration_minutes: 15,
        })
            .then(response => history.push(`/teacher/course/${courseId}/lesson/${response.data.id}/description`))
            .catch(() => showMessage({ text: 'Could not create lesson.', severity: 'error' }))
            .finally(() => setSaving(false));
    }

    function handleCopyStudentSignup() {
        if (!studentSignupUrl) return;
        copyToClipboard(studentSignupUrl)
            .then(() => showMessage({ text: 'Class signup link copied.', severity: 'success' }))
            .catch(() => showMessage({ text: 'Could not copy signup link.', severity: 'error' }));
    }

    function handleLessonReorder(reordered: IVerbaScholaLesson[]) {
        runAction(
            () => Promise.all(reordered.map((lesson, index) => VerbaScholaApi.updateLesson(lesson.id, { order_index: index + 1 }))),
            'Lesson order updated.',
        );
    }

    function handleSetLessonAvailable(lesson: IVerbaScholaLesson, available: boolean) {
        if (!available && !window.confirm('Hide this lesson from students? Their saved work will remain, but they cannot open the lesson until you make it available again.')) return;
        runAction(
            () => VerbaScholaApi.updateLesson(lesson.id, { status: available && lesson.status === 'draft' ? 'published' : lesson.status, is_hidden: !available,
                ...(available ? { require_playable_exercises: true } : {}),
                expected_authoring_revision: lesson.authoring_revision }),
            available ? 'Lesson is now available to students.' : 'Lesson is now hidden from students.',
            'use Resend invitation if needed.', lesson.id,
        );
    }

    return (
        <VerbaScholaTeacherShell
            title={headerTitle}
            titleAdornment={course ? <Chip label={course.is_archived ? 'Archived' : 'Active'} size="small" sx={statusChipSx(course.is_archived ? 'pending' : 'accepted')} /> : undefined}
            subtitle={course ? courseSubtitle(course, summary?.student_count) : null}
            headerVariant="workspace"
            loading={(active.loading && active.data == null) || (overview.loading && overview.data == null)}
            refreshing={(active.loading && active.data != null) || (overview.loading && overview.data != null)}
            error={dataShapeError ?? active.error ?? overview.error}
            onRetry={active.reload}
            actions={
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignSelf: { xs: 'flex-end', md: 'auto' } }}>
                    {course && (
                        <Button
                            startIcon={<ContentCopyRoundedIcon />}
                            disabled={!studentSignupUrl}
                            onClick={handleCopyStudentSignup}
                            sx={{ fontFamily: INTER, fontWeight: 700, color: BLUE, textTransform: 'none' }}
                        >
                            Copy signup link
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<AutoAwesomeRoundedIcon />}
                        disabled={saving}
                        onClick={handleCreateLesson}
                        sx={{ ...aiButtonSx, borderRadius: '11px', px: 2.25, textTransform: 'none' }}
                    >
                        Create lesson
                    </Button>
                </Stack>
            }
            navigation={
                <Tabs
                    value={activeTab}
                    onChange={(_event, value: CourseTab) => history.push(`/teacher/course/${courseId}/${value}`)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mt: 2.5, minHeight: 44, '& .MuiTab-root': { minHeight: 44, minWidth: 0, mr: { xs: 2.25, sm: 3.5 }, px: 0, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, textTransform: 'none', color: SLATE }, '& .Mui-selected': { color: `${BLUE} !important` }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: BLUE } }}
                >
                    {TAB_LIST.map(tab => <Tab key={tab.value} value={tab.value} label={tab.label} />)}
                </Tabs>
            }
        >

            <RouterSwitch>
                <Route
                    exact
                    path="/teacher/course/:courseId/overview"
                    render={() => summary ? (
                        <VerbaScholaTeacherCourseOverviewPage
                            courseName={course?.name ?? 'this class'}
                            summary={summary}
                            groups={overviewLessonRows}
                            onOpenLesson={lessonId => history.push(`/teacher/course/${courseId}/lesson/${lessonId}/overview`)}
                            onOpenReview={lessonId => history.push(`/teacher/course/${courseId}/lesson/${lessonId}/speaking-review`)}
                            onOpenTab={tab => history.push(`/teacher/course/${courseId}/${tab}`)}
                        />
                    ) : null}
                />
                <Route
                    exact
                    path="/teacher/course/:courseId/lesson"
                    render={() => (
                        <VerbaScholaTeacherCourseLessonsPage
                            lessons={lessonRows}
                            lessonGroupsById={lessonGroupsById}
                            summary={summary}
                            saving={saving}
                            onReorder={handleLessonReorder}
                            onSetLessonAvailable={handleSetLessonAvailable}
                            onOpenLesson={lessonId => history.push(`/teacher/course/${courseId}/lesson/${lessonId}/overview`)}
                        />
                    )}
                />
                <Route
                    exact
                    path="/teacher/course/:courseId/student"
                    render={() => (
                        <VerbaScholaTeacherCourseStudentsPage
                            courseId={courseId}
                            enrollments={studentRows}
                            lessonGroups={overviewLessonRows}
                            saving={saving}
                            onAction={runAction}
                        />
                    )}
                />
                <Route
                    exact
                    path="/teacher/course/:courseId/settings"
                    render={() => course ? (
                        <VerbaScholaTeacherCourseSettingsPage
                            course={course}
                            teacherList={teacherRows}
                            saving={saving}
                            studentSignupUrl={studentSignupUrl}
                            onNamePreview={handleNamePreview}
                            onSave={payload => runAction(() => VerbaScholaApi.updateCourse(courseId, payload), 'Course settings saved.')}
                            onCopyStudentSignup={handleCopyStudentSignup}
                            onInviteTeacher={teacher => runAction(() => VerbaScholaApi.createTeacherCourse(courseId, { ...teacher, status: 'accepted' }), 'Teacher invited.')}
                            onUpdateTeacher={(teacherCourseId, teacher) => runAction(() => VerbaScholaApi.updateTeacherCourse(teacherCourseId, teacher), 'Teacher details saved.')}
                            onRemoveTeacher={teacherCourseId => runAction(() => VerbaScholaApi.deleteTeacherCourse(teacherCourseId), 'Teacher removed.')}
                        />
                    ) : null}
                />
                <Redirect to={`/teacher/course/${courseId}/overview`} />
            </RouterSwitch>

            <Snackbar open={message !== null} autoHideDuration={pictureReviewLessonId !== null ? null : 3000} onClose={() => showMessage(null)}>
                <Alert severity={message?.severity ?? 'success'} onClose={() => showMessage(null)}
                    action={pictureReviewLessonId !== null ? <Button color="inherit" onClick={() => history.push(`/teacher/course/${courseId}/lesson/${pictureReviewLessonId}/exercises`)}>Review exercises</Button> : undefined}
                    sx={{ fontFamily: INTER }}>
                    {message?.text}
                </Alert>
            </Snackbar>
        </VerbaScholaTeacherShell>
    );
}
